import { z } from 'zod';
import { equivalenceKindSchema, oddsPhaseSchema } from './sports-verifier.js';
const nonEmptyStringSchema = z.string().trim().min(1);
const timestampSchema = z.string().trim().min(1);
const positiveIntSchema = z.number().int().positive();
const nonNegativeIntSchema = z.number().int().min(0);
const basisPointsSchema = z.number().int().min(0).max(10000);
export const liveOddsMarketSectionSchema = z.enum(['moneyline', 'spread', 'total_goals', 'both_teams_to_score']);
export const liveOddsReviewStateSchema = z.enum(['candidate', 'reviewed', 'disabled', 'archived']);
export const liveOddsSideSchema = z.enum(['home', 'draw', 'away']);
export const liveOddsRefreshModeSchema = z.enum(['preview', 'dry_run', 'write']);
export const liveOddsEvidenceFreshnessSchema = z.enum([
    'fresh',
    'stale',
    'missing_equivalent',
    'provider_failed',
    'rate_limited',
    'invalid_payload',
    'unsupported_market',
]);
export const liveOddsAvailabilityReasonSchema = z.enum([
    'fresh',
    'stale',
    'provider_market_not_open',
    'missing_equivalent',
    'provider_failed',
    'rate_limited',
    'invalid_payload',
    'unsupported_market',
    'skipped_by_budget',
    'polymarket_price_unavailable',
]);
export const mappingCandidateReviewStatusSchema = z.enum(['pending', 'approved', 'rejected', 'needs_review']);
export const liveOddsAutomationEligibilityProfileSchema = z.enum([
    'all_safe',
    'equivalent_only',
    'moneyline_equivalent',
]);
export const liveOddsAutomationProfileIdSchema = z.enum([
    'safe_8000',
    'safe_9000',
    'moneyline_equivalent_6000',
    'moneyline_equivalent_8000',
    'custom_equivalent',
]);
export const liveOddsAutomationPhaseStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'skipped']);
export const aggregateReferenceSourceTypeSchema = z.enum(['reference_bookmakers', 'all_fresh_observations']);
export const liveOddsDifferenceDirectionSchema = z.enum(['lower', 'higher', 'aligned']);
export const radarLiveOddsComparisonStatusSchema = z.enum(['available', 'unavailable']);
export const liveOddsRunTriggerSchema = z.enum(['manual', 'scheduled', 'retry']);
export const liveOddsAutomationRunStatusSchema = z.enum([
    'accepted',
    'claimed',
    'running',
    'completed',
    'failed',
    'rejected',
    'invalidated',
    'interrupted',
]);
export const automationRunRecoveryDispositionSchema = z.enum([
    'preview_accepted',
    'wait_for_run',
    'new_preview_required',
    'run_completed',
    'run_failed',
    'run_interrupted',
    'run_rejected',
]);
export const automationRunRecoveryInstructionSchema = z.enum([
    'execute_accepted_preview',
    'poll_running_run',
    'create_new_preview',
    'inspect_completed_run',
    'inspect_failed_run',
    'inspect_interrupted_run',
    'inspect_rejected_run',
]);
export const automationRunRecoveryViewSchema = z.object({
    runId: nonEmptyStringSchema,
    status: liveOddsAutomationRunStatusSchema,
    disposition: automationRunRecoveryDispositionSchema,
    reason: z.string().nullable(),
    instruction: automationRunRecoveryInstructionSchema,
    requiresNewPreview: z.boolean(),
    waitPollingAllowed: z.boolean(),
});
export const automationPreviewOwnershipRecoverySchema = z.object({
    disposition: z.literal('preview_not_owned'),
    instruction: z.literal('create_actor_preview'),
    requiresNewPreview: z.literal(true),
    waitPollingAllowed: z.literal(false),
});
export const liveOddsPlanOwnershipResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        issues: z.array(z.object({
            code: nonEmptyStringSchema,
            path: z.array(z.union([z.string(), z.number()])),
            message: z.string().optional(),
        })).min(1),
        name: nonEmptyStringSchema,
    }),
    ownershipRecovery: automationPreviewOwnershipRecoverySchema,
});
export const liveOddsLifecycleConflictResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        issues: z.array(z.object({
            code: nonEmptyStringSchema,
            path: z.array(z.union([z.string(), z.number()])),
            message: z.string().optional(),
        })).min(1),
        name: nonEmptyStringSchema,
    }),
    recoveryView: automationRunRecoveryViewSchema,
});
export const liveOddsRefreshScopeSchema = z.object({
    competitionCode: nonEmptyStringSchema.optional().nullable().default(null),
    season: positiveIntSchema.optional().nullable().default(null),
    kickoffFrom: timestampSchema.optional().nullable().default(null),
    kickoffTo: timestampSchema.optional().nullable().default(null),
});
const liveOddsRefreshRequestBudgetSchema = z.number().int().positive().max(7500);
export const liveOddsRefreshPreviewRequestSchema = z.object({
    scope: liveOddsRefreshScopeSchema,
    requestBudget: liveOddsRefreshRequestBudgetSchema,
});
export const liveOddsPlanRequestEstimateSchema = z.object({
    statusProbe: nonNegativeIntSchema,
    polymarketPrice: nonNegativeIntSchema,
    fixtureOdds: nonNegativeIntSchema,
    retries: nonNegativeIntSchema,
    total: nonNegativeIntSchema,
});
export const liveOddsAutomationPlanTargetSchema = z.object({
    targetPlanId: nonEmptyStringSchema,
    mappingId: nonEmptyStringSchema,
    candidateId: nonEmptyStringSchema.nullable(),
    reviewedEquivalenceId: nonEmptyStringSchema,
    fixtureMappingId: nonEmptyStringSchema,
    order: nonNegativeIntSchema,
    selected: z.boolean(),
    omissionReason: nonEmptyStringSchema.nullable(),
    requestEstimate: liveOddsPlanRequestEstimateSchema,
    effects: z.array(nonEmptyStringSchema),
    targetSnapshot: z.record(z.string(), z.unknown()),
});
export const liveOddsCandidateApprovalPlanSchema = z.object({
    candidateId: nonEmptyStringSchema,
    order: nonNegativeIntSchema,
    reviewedEquivalenceId: nonEmptyStringSchema,
    fixtureMappingId: nonEmptyStringSchema,
    targetMappingId: nonEmptyStringSchema,
});
export const liveOddsCandidateOmissionPlanSchema = z.object({
    candidateId: nonEmptyStringSchema,
    order: nonNegativeIntSchema,
    reason: nonEmptyStringSchema,
});
export const liveOddsAutomationPlanResponseSchema = z.object({
    automationRunId: nonEmptyStringSchema,
    previewId: nonEmptyStringSchema,
    generatedAt: timestampSchema,
    status: z.literal('accepted'),
    accepted: z.literal(true),
    trigger: liveOddsRunTriggerSchema,
    policyVersion: nonEmptyStringSchema,
    scope: liveOddsRefreshScopeSchema,
    requestBudget: liveOddsRefreshRequestBudgetSchema,
    estimatedProviderRequests: nonNegativeIntSchema,
    candidateApprovals: z.array(liveOddsCandidateApprovalPlanSchema),
    candidateOmissions: z.array(liveOddsCandidateOmissionPlanSchema),
    selectedTargets: z.array(liveOddsAutomationPlanTargetSchema),
    omittedTargets: z.array(liveOddsAutomationPlanTargetSchema),
    retryOfAutomationRunId: nonEmptyStringSchema.nullable(),
    recoveryView: automationRunRecoveryViewSchema,
});
export const liveOddsRefreshPreviewResponseSchema = liveOddsAutomationPlanResponseSchema;
export const liveOddsRefreshAcceptedPreviewSchema = z.object({
    previewId: nonEmptyStringSchema,
    scope: liveOddsRefreshScopeSchema,
    requestBudget: liveOddsRefreshRequestBudgetSchema,
});
export const liveOddsPlanClaimSchema = liveOddsRefreshAcceptedPreviewSchema.pick({ previewId: true });
export const liveOddsRefreshWriteRequestSchema = z.object({
    acceptedPreview: liveOddsPlanClaimSchema,
});
export const liveOddsRetryPreviewRequestSchema = z.object({
    sourceAutomationRunId: nonEmptyStringSchema,
    requestBudget: liveOddsRefreshRequestBudgetSchema,
});
export const liveOddsAutomationProfileSchema = z.object({
    profileId: liveOddsAutomationProfileIdSchema,
    label: nonEmptyStringSchema,
    description: nonEmptyStringSchema,
    minConfidence: z.number().int().min(0).max(10000),
    eligibilityProfile: liveOddsAutomationEligibilityProfileSchema,
    allowsCustomMinConfidence: z.boolean().default(false),
    defaultMaxCandidates: z.number().int().positive().max(1000).default(200),
    defaultRequestBudget: liveOddsRefreshRequestBudgetSchema.default(300),
});
export const liveOddsAutomationProfilesResponseSchema = z.object({
    profiles: z.array(liveOddsAutomationProfileSchema),
});
export const liveOddsAutomationRunRequestSchema = z.object({
    profileId: liveOddsAutomationProfileIdSchema.optional(),
    minConfidence: z.number().int().min(0).max(10000).optional().default(8000),
    eligibilityProfile: liveOddsAutomationEligibilityProfileSchema.optional().default('all_safe'),
    maxCandidates: z.number().int().positive().max(1000).optional().default(200),
    requestBudget: liveOddsRefreshRequestBudgetSchema.optional().default(300),
    scope: liveOddsRefreshScopeSchema,
});
export const liveOddsAutomationPreviewRequestSchema = liveOddsAutomationRunRequestSchema;
export const liveOddsFixtureAvailabilitySchema = z.object({
    refreshRunId: nonEmptyStringSchema.optional(),
    apiFootballFixtureId: positiveIntSchema,
    reason: liveOddsAvailabilityReasonSchema,
    targetCount: nonNegativeIntSchema,
    checkedAt: timestampSchema.optional(),
});
export const liveOddsTargetAvailabilitySchema = z.object({
    refreshRunId: nonEmptyStringSchema.optional(),
    mappingId: nonEmptyStringSchema,
    apiFootballFixtureId: positiveIntSchema,
    reason: liveOddsAvailabilityReasonSchema,
    checkedAt: timestampSchema.optional(),
});
export const liveOddsRefreshRunSummarySchema = z.object({
    refreshRunId: nonEmptyStringSchema,
    trigger: liveOddsRunTriggerSchema,
    status: z.enum(['running', 'completed', 'partial', 'failed', 'invalidated', 'interrupted']),
    mode: liveOddsRefreshModeSchema,
    previewId: nonEmptyStringSchema.nullable(),
    scope: liveOddsRefreshScopeSchema,
    requestBudget: liveOddsRefreshRequestBudgetSchema,
    requestCount: nonNegativeIntSchema,
    fetchedFixtureCount: nonNegativeIntSchema,
    targetCount: nonNegativeIntSchema,
    reasonCounts: z.record(z.string(), nonNegativeIntSchema),
    startedAt: timestampSchema,
    completedAt: timestampSchema.nullable(),
});
export const liveOddsRefreshRunListResponseSchema = z.object({
    runs: z.array(liveOddsRefreshRunSummarySchema),
});
export const liveOddsTargetResultCountsSchema = z.object({
    success: nonNegativeIntSchema,
    failed: nonNegativeIntSchema,
    deferred: nonNegativeIntSchema,
    budgetOmitted: nonNegativeIntSchema,
});
export const liveOddsRefreshExecutionResponseSchema = z.object({
    automationRunId: nonEmptyStringSchema,
    previewId: nonEmptyStringSchema,
    refreshRunId: nonEmptyStringSchema,
    status: z.enum(['completed', 'partial', 'failed']),
    scope: liveOddsRefreshScopeSchema,
    requestBudget: liveOddsRefreshRequestBudgetSchema,
    requestCount: nonNegativeIntSchema,
    payloadArchiveCount: nonNegativeIntSchema,
    observationCount: nonNegativeIntSchema,
    comparisonCount: nonNegativeIntSchema,
    targetResultCounts: liveOddsTargetResultCountsSchema,
});
export const liveOddsLatestAvailabilityResponseSchema = z.object({
    generatedAt: timestampSchema,
    fixtureAvailability: z.array(liveOddsFixtureAvailabilitySchema),
    targetAvailability: z.array(liveOddsTargetAvailabilitySchema),
});
export const liveOddsTargetSchema = z.object({
    mappingId: nonEmptyStringSchema,
    reviewedEquivalenceId: nonEmptyStringSchema.nullable().optional(),
    polymarketEventId: nonEmptyStringSchema,
    polymarketMarketId: nonEmptyStringSchema,
    polymarketOutcomeId: nonEmptyStringSchema,
    polymarketOutcomeLabel: nonEmptyStringSchema,
    polymarketOutcomeIndex: nonNegativeIntSchema,
    polymarketClobTokenId: nonEmptyStringSchema,
    apiFootballFixtureId: positiveIntSchema,
    competitionCode: nonEmptyStringSchema,
    season: positiveIntSchema,
    apiFootballBetId: positiveIntSchema,
    apiFootballBetName: nonEmptyStringSchema,
    apiFootballValue: nonEmptyStringSchema,
    oddsPhase: oddsPhaseSchema,
    marketSection: liveOddsMarketSectionSchema,
    side: liveOddsSideSchema.nullable().default(null),
    lineValue: z.number().finite().nullable().default(null),
    equivalenceKind: equivalenceKindSchema,
    reviewState: liveOddsReviewStateSchema,
    enabled: z.boolean().default(true),
    labels: z.object({
        event: nonEmptyStringSchema,
        outcome: nonEmptyStringSchema,
    }),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const mappingCandidateSchema = z.object({
    candidateId: nonEmptyStringSchema,
    polymarketEventId: nonEmptyStringSchema,
    polymarketMarketId: nonEmptyStringSchema,
    polymarketOutcomeId: nonEmptyStringSchema,
    polymarketOutcomeLabel: nonEmptyStringSchema,
    polymarketOutcomeIndex: nonNegativeIntSchema,
    polymarketClobTokenId: nonEmptyStringSchema,
    apiFootballFixtureId: positiveIntSchema,
    competitionCode: nonEmptyStringSchema,
    season: positiveIntSchema,
    suggestedMarketSection: liveOddsMarketSectionSchema,
    apiFootballBetId: positiveIntSchema,
    apiFootballBetName: nonEmptyStringSchema,
    suggestedProviderValue: nonEmptyStringSchema,
    suggestedSide: liveOddsSideSchema.nullable().default(null),
    suggestedLineValue: z.number().finite().nullable().default(null),
    equivalenceKind: equivalenceKindSchema,
    confidenceScore: z.number().int().min(0).max(10000),
    matchReasons: z.array(nonEmptyStringSchema),
    conflictReasons: z.array(nonEmptyStringSchema),
    reviewStatus: mappingCandidateReviewStatusSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const bookmakerOddsObservationSchema = z.object({
    evidenceId: nonEmptyStringSchema,
    mappingId: nonEmptyStringSchema,
    apiFootballFixtureId: positiveIntSchema,
    bookmakerId: positiveIntSchema,
    bookmakerName: nonEmptyStringSchema,
    betId: positiveIntSchema,
    providerValue: nonEmptyStringSchema,
    decimalOdds: z.number().finite().gt(1),
    traditionalImpliedProbabilityBps: basisPointsSchema,
    normalizedTraditionalImpliedProbabilityBps: basisPointsSchema.nullable().default(null),
    polymarketImpliedProbabilityBps: basisPointsSchema,
    decimalOddsEquivalent: z.number().finite().gt(1).nullable().default(null),
    fetchedAt: timestampSchema,
    providerTimestamp: timestampSchema.nullable().default(null),
    freshnessState: liveOddsEvidenceFreshnessSchema,
    rawPayloadArchiveId: nonEmptyStringSchema,
    normalizationVersion: nonEmptyStringSchema,
    providerFailureReason: nonEmptyStringSchema.nullable().default(null),
});
export const aggregateOddsReferenceSchema = z.object({
    probabilityBps: basisPointsSchema,
    sourceCount: positiveIntSchema,
    sourceType: aggregateReferenceSourceTypeSchema,
    bookmakerNames: z.array(nonEmptyStringSchema),
    computedAt: timestampSchema,
});
export const radarLiveOddsComparisonSchema = z.object({
    status: radarLiveOddsComparisonStatusSchema,
    polymarketEventId: nonEmptyStringSchema,
    polymarketMarketId: nonEmptyStringSchema,
    polymarketOutcomeId: nonEmptyStringSchema,
    polymarketImpliedProbabilityBps: basisPointsSchema.nullable(),
    polymarketDecimalOddsEquivalent: z.number().finite().gt(1).nullable(),
    aggregateReferenceProbabilityBps: basisPointsSchema.nullable(),
    signedProbabilityDifferenceBps: z.number().int().min(-10000).max(10000).nullable(),
    direction: liveOddsDifferenceDirectionSchema.nullable(),
    sourceLabels: z.array(nonEmptyStringSchema),
    freshnessState: liveOddsEvidenceFreshnessSchema.nullable(),
    lastUpdatedAt: timestampSchema.nullable(),
    unavailableReason: liveOddsEvidenceFreshnessSchema.nullable(),
});
export const liveOddsAdminReadinessResponseSchema = z.object({
    generatedAt: timestampSchema,
    reviewedTargetCount: nonNegativeIntSchema,
    mappedFixtureCount: nonNegativeIntSchema,
    targetsMissingFreshEvidenceCount: nonNegativeIntSchema,
    providerFailureCount: nonNegativeIntSchema,
    candidateCount: nonNegativeIntSchema,
    pendingCandidateCount: nonNegativeIntSchema,
    unavailableEvidenceByReason: z.array(z.object({
        reason: liveOddsEvidenceFreshnessSchema,
        count: nonNegativeIntSchema,
    })),
    inplayBudget: z.object({
        dailyBudget: positiveIntSchema,
        eightMatchDayEstimatedRequests: nonNegativeIntSchema,
        endpointCounts: z.record(z.string(), nonNegativeIntSchema),
        downgradeOrder: z.array(nonEmptyStringSchema),
        predictionsExcludedDuringLive: z.boolean(),
    }).optional(),
});
export const liveOddsReadinessQuerySchema = z.object({
    competitionCode: nonEmptyStringSchema.optional(),
    season: z.coerce.number().int().positive().optional(),
});
export const liveOddsAutomationPhaseSchema = z.object({
    status: liveOddsAutomationPhaseStatusSchema,
    startedAt: timestampSchema.nullable().optional().default(null),
    completedAt: timestampSchema.nullable().optional().default(null),
    errorMessage: nonEmptyStringSchema.nullable().optional().default(null),
});
export const liveOddsAutomationPhasesSchema = z.object({
    approval: liveOddsAutomationPhaseSchema,
    refreshPreview: liveOddsAutomationPhaseSchema,
    refreshWrite: liveOddsAutomationPhaseSchema,
});
export const liveOddsAutomationPreviewResponseSchema = z.object({
    generatedAt: timestampSchema,
    profileId: liveOddsAutomationProfileIdSchema,
    minConfidence: z.number().int().min(0).max(10000),
    eligibilityProfile: liveOddsAutomationEligibilityProfileSchema,
    maxCandidates: positiveIntSchema,
    eligibleCandidateCount: nonNegativeIntSchema,
    skippedCandidateCount: nonNegativeIntSchema,
    skippedByReason: z.record(z.string(), nonNegativeIntSchema),
    approvedBySection: z.record(z.string(), nonNegativeIntSchema),
});
export const liveOddsAutomationRunResponseSchema = z.object({
    generatedAt: timestampSchema,
    automationRunId: nonEmptyStringSchema.optional(),
    profileId: liveOddsAutomationProfileIdSchema.optional(),
    minConfidence: z.number().int().min(0).max(10000),
    eligibilityProfile: liveOddsAutomationEligibilityProfileSchema,
    maxCandidates: positiveIntSchema,
    eligibleCandidateCount: nonNegativeIntSchema,
    approvedCandidateCount: nonNegativeIntSchema,
    skippedCandidateCount: nonNegativeIntSchema,
    skippedByReason: z.record(z.string(), nonNegativeIntSchema).optional().default({}),
    approvedBySection: z.record(z.string(), nonNegativeIntSchema),
    phases: liveOddsAutomationPhasesSchema.optional(),
    refreshRunIds: z.object({
        previewId: nonEmptyStringSchema.nullable().default(null),
        refreshRunId: nonEmptyStringSchema.nullable().default(null),
    }).optional(),
    refresh: liveOddsRefreshExecutionResponseSchema,
});
export const liveOddsCandidateListQuerySchema = z.object({
    reviewStatus: mappingCandidateReviewStatusSchema.optional(),
    competitionCode: nonEmptyStringSchema.optional(),
    season: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().positive().max(200).optional().default(100),
});
export const liveOddsBatchApproveCandidatesRequestSchema = z.object({
    candidateIds: z.array(nonEmptyStringSchema).min(1).max(200),
    competitionCode: nonEmptyStringSchema,
    season: positiveIntSchema,
});
export const liveOddsBatchApproveCandidatesResponseSchema = z.object({
    approvedCount: nonNegativeIntSchema,
    skippedCount: nonNegativeIntSchema,
    createdMappingIds: z.array(nonEmptyStringSchema),
    validationErrors: z.array(z.object({
        candidateId: nonEmptyStringSchema,
        message: nonEmptyStringSchema,
    })),
});
export const liveOddsUpdateCandidateStatusRequestSchema = z.object({
    candidateIds: z.array(nonEmptyStringSchema).min(1).max(200),
    competitionCode: nonEmptyStringSchema,
    season: positiveIntSchema,
    reviewStatus: z.enum(['rejected', 'needs_review']),
});
export const liveOddsUpdateCandidateStatusResponseSchema = z.object({
    updatedCount: nonNegativeIntSchema,
});
export const liveOddsRadarEvidenceQuerySchema = z.object({
    polymarketEventId: nonEmptyStringSchema,
    polymarketMarketId: nonEmptyStringSchema.optional(),
    polymarketOutcomeId: nonEmptyStringSchema.optional(),
});
